// src/oura/newscan/fixtures/words.ts
//
// The words that write the 50: where each value comes from (a staff edit, the
// sample's pick, or what the Business step gave), the other picks, and the
// Words view list built from the rows that use them.

import type { ScanDraftBusiness, ScanDraftWord, WordOrigin } from "@service/aeo/scanDraftTypes";
import { CATEGORY_CAP, LANGUAGE_TIER_1, PLACE_VARS, wordFamily, wordLabel } from "./recipe";
import { fallbackWords, type FixtureSample, type FixtureWord } from "./samples";
import type { AssembledRow } from "./model";

export interface WordSource {
    /** The value the 50 use now: the staff's edit, else the pick. */
    value(variable: string): string;
    /** The draft's own pick, ignoring staff edits. */
    pick(variable: string): string;
    edited(variable: string): boolean;
    origin(variable: string): WordOrigin;
    /** Other picks, best first, never the current value. */
    alternatives(variable: string): string[];
}

function uniq(values: string[]): string[] {
    const seen = new Set<string>();
    return values.filter((v) => {
        const k = v.toLowerCase();
        if (!v || seen.has(k)) return false;
        seen.add(k);
        return true;
    });
}

export function wordSource(
    sample: FixtureSample | null,
    built: ScanDraftBusiness,
    edits: Record<string, string>,
    round: number,
): WordSource {
    const fallback = fallbackWords(built);
    const meta = (v: string): FixtureWord | null => sample?.wordsByBuy?.[built.buy]?.[v] ?? sample?.words[v] ?? fallback[v] ?? null;
    const pick = (v: string): string => {
        const m = meta(v);
        if (!m || m.missing) return "";
        if (!round || !m.alternatives.length) return m.value;
        const pool = [m.value, ...m.alternatives];
        return pool[round % pool.length];
    };
    const edited = (v: string) => Object.prototype.hasOwnProperty.call(edits, v);
    const value = (v: string) => (edited(v) ? edits[v] : pick(v));
    return {
        value,
        pick,
        edited,
        origin: (v) => (edited(v) ? "staff" : meta(v)?.origin ?? "fallback"),
        alternatives: (v) => {
            const m = meta(v);
            if (!m) return [];
            const current = value(v).toLowerCase();
            return uniq([m.missing ? "" : m.value, ...m.alternatives]).filter((x) => x.toLowerCase() !== current);
        },
    };
}

/** Malay / Chinese rows: On, or Auto when the sample's share reaches tier 1. */
export function languagesOn(built: ScanDraftBusiness, sample: FixtureSample | null): { ms: boolean; zh: boolean } {
    const share = sample?.languageShare ?? { ms: 0, zh: 0 };
    const on = (mode: string, s: number) => mode === "on" || (mode === "auto" && s >= LANGUAGE_TIER_1);
    return { ms: on(built.languages.ms, share.ms), zh: on(built.languages.zh, share.zh) };
}

/** The Words view: one entry per variable the 50 use, in order of first use. */
export function buildWords(rows: AssembledRow[], src: WordSource): ScanDraftWord[] {
    const order: string[] = [];
    const usedBy: Record<string, number[]> = {};
    const placeValues: Record<string, string[]> = {};
    rows.forEach(({ question }) => {
        question.fill_vars.forEach((v) => {
            if (!usedBy[v]) {
                usedBy[v] = [];
                order.push(v);
            }
            usedBy[v].push(question.n);
            if (PLACE_VARS.has(v)) {
                const val = question.fill_values[v];
                placeValues[v] = uniq([...(placeValues[v] ?? []), val ?? ""]);
            }
        });
    });
    return order.map((variable): ScanDraftWord => {
        const place = PLACE_VARS.has(variable);
        const value = place ? (placeValues[variable] ?? []).join(", ") : src.value(variable);
        const used = rows.filter((r) => value && r.question.text.toLowerCase().includes(value.toLowerCase())).length;
        return {
            variable,
            label: wordLabel(variable),
            value,
            family: wordFamily(variable),
            usedBy: usedBy[variable],
            editable: !place,
            origin: place ? "site" : src.origin(variable),
            alternatives: place ? [] : src.alternatives(variable),
            capWarn: variable === "category" ? { used, warnAbove: CATEGORY_CAP.warnAbove, failAbove: CATEGORY_CAP.failAbove } : null,
        };
    });
}
